import { useMemo } from "react";
import { Typography } from "@/components/atoms";
import { useGetVehicleTypesQuery } from "@/redux/apis/config/trip/vehicleType/vehicleTypeApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { clsx } from "@/utils";

interface VehicleTypeCellProps {
  vehicle: Vehicle;
  onShowTypes?: (vehicle: Vehicle) => void;
  emptyFallback?: JSX.Element;
  singleTypeClassName?: string;
}

/**
 * The attached types a read-only surface should display: only types that are
 * currently ACTIVE in the provider's catalog. Attached-but-inactive types
 * cannot receive bookings, so showing them misleads (e.g. "1 Vehicle Type"
 * where that one type is switched off).
 *
 * Status source: the vehicle payload's embedded is_active (API ≥1.1.1) when
 * every entry carries it; otherwise falls back to a provider-catalog lookup
 * (older payloads) — in which case undefined is returned while it loads.
 */
export const useVisibleVehicleTypes = (vehicle: Vehicle) => {
  const attached = vehicle.vehicleTypes?.length ? vehicle.vehicleTypes : vehicle.vehicleType ? [vehicle.vehicleType] : [];
  const hasEmbeddedStatus = attached.length === 0 || attached.every((type) => typeof type.isActive === "boolean");

  const { data } = useGetVehicleTypesQuery({ page: "1", page_size: "200" }, { skip: hasEmbeddedStatus });

  return useMemo(() => {
    if (hasEmbeddedStatus) {
      return attached.filter((type) => type.isActive === true);
    }
    if (!data) return undefined;
    const activeUuids = new Set(data.vehicleTypes.filter((type) => type.isActive).map((type) => type.uuid));
    return attached.filter((type) => activeUuids.has(type.uuid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, vehicle]);
};

/**
 * Shared "Vehicle Type" cell for every read-only vehicle surface. A single
 * visible (active) type shows its name; two or more collapse into a
 * "+N Vehicle Types" link (opens the vehicle-types modal via `onShowTypes`);
 * zero visible types render `emptyFallback` (default "-") even when inactive
 * attachments exist. The Edit Vehicle multi-select deliberately still lists
 * inactive attachments — that is the surface for managing them.
 */
export const VehicleTypeCell = ({ vehicle, onShowTypes, emptyFallback, singleTypeClassName }: VehicleTypeCellProps) => {
  const visibleTypes = useVisibleVehicleTypes(vehicle);

  // Catalog status still loading — render nothing rather than a wrong count.
  if (!visibleTypes) return null;

  if (visibleTypes.length === 0) {
    return emptyFallback ?? <Typography className="line-clamp-1">-</Typography>;
  }

  if (visibleTypes.length === 1) {
    return <Typography className={clsx("line-clamp-1", singleTypeClassName)}>{visibleTypes[0].name}</Typography>;
  }

  const label = `+${visibleTypes.length} Vehicle Types`;

  if (!onShowTypes) {
    return <Typography className="line-clamp-1">{label}</Typography>;
  }

  return (
    <button type="button" onClick={() => onShowTypes(vehicle)} className="self-start text-left text-info underline">
      {label}
    </button>
  );
};
